import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import { message } from 'antd'
import { reportClientError } from './errorLogService'

// API基础配置
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || ''

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

const unwrapApiPayload = <T>(data: T | { code?: string; data?: T }) => {
  if (data && typeof data === 'object' && 'code' in data) {
    return (data as { data?: T }).data as T
  }
  return data as T
}

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    const { data } = response
    // 统一处理响应格式
    if (data.code && data.code !== 'SUCCESS') {
      message.error(data.message || '操作失败')
      return Promise.reject(data)
    }
    return unwrapApiPayload(data)
  },
  (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response as any
      
      // 处理401未授权
      if (status === 401) {
        message.error('登录已过期，请重新登录')
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(error)
      }
      
      // 处理其他错误（FastAPI HTTPException 通常是 {detail: ...}）
      const detailMessage =
        typeof data?.detail === 'string'
          ? data.detail
          : (data?.detail && typeof data.detail === 'object' && (data.detail.message as string | undefined)) || undefined
      message.error(data?.message || detailMessage || `请求失败: ${status}`)
      if (status >= 500) {
        void reportClientError({
          message: data?.message || detailMessage || `HTTP ${status}`,
          stack: error.stack,
          metadata: {
            status,
            method: error.config?.method,
            request_url: error.config?.url,
            response: data,
          },
        })
      }
    } else {
      message.error('网络错误，请检查网络连接')
      void reportClientError({
        message: error.message || 'Network error',
        stack: error.stack,
        metadata: {
          method: error.config?.method,
          request_url: error.config?.url,
        },
      })
    }
    return Promise.reject(error)
  }
)

// 通用请求方法
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<any, T>(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.post<any, T>(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.put<any, T>(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<any, T>(url, config),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.patch<any, T>(url, data, config),
}

export default api
